// Pure text helpers for Smart Add and the gift forms. No DOM, no Firebase.

const BULLET = /^\s*(?:[-*•·▪◦‣✓✔☐☑✅]|\d{1,2}[.)]|\(\d{1,2}\))\s+/;
const URL_RE = /https?:\/\/[^\s<>"')\]]+/i;
const TLD = 'com|net|org|io|co|us|shop|store|app|edu|gov|me|ly|to|tv|xyz|info|biz|ca|uk|de|fr|au|ai|gg|link|page|site|online|dev|be|it|es|nl|gl|sh|cc|fm|ch|at|ie|mx|br|jp|nz|se|dk|fi|pl|za';
// Bare domains like hotdogs.com or www.etsy.com/listing/1. Group 1 is the character before, group 2 the domain.
const DOMAIN_RE = new RegExp(
  '(^|[\\s(\\[<,;"\'])((?:www\\.)?(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+(?:' + TLD + ')(?:\\/[^\\s,;)\\]>"\']*)?)(?![\\w@-])',
  'i'
);
// What is left of a line once its link is removed, when the line was only a label for that link.
const LINKLABEL = /^(also|link|the link|here'?s? the link|link for [^:]*|website|url|buy it here|buy here|order here|shop here)\s*:?$/i;
const JUNK = /^(delivered|read|seen|sent|today|yesterday|\d{1,2}:\d{2}\s?(am|pm)?|thanks?!*|thank you!*|thx!*|xo+|love you!*|merry christmas!*|sent from my .*|(hi|hello|hey)( there| everyone| all)?[!,.]*|(hi|hello|hey) \w+[!,]+)$/i;

export const INVALID_LINK_MESSAGE = 'That link doesn’t look right. Paste the full address, starting with https://';

export function normKey(t) {
  return String(t || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function stripTracking(u) {
  return u.replace(/([?&])utm_[^&#\s]*&?/gi, '$1').replace(/[?&]+(?=#|$)/, '');
}

function cleanLink(u) {
  return stripTracking(u.replace(/[.,;:!?]+$/, ''));
}

// '' for empty input, a clean http(s) URL for a valid link or bare domain, null for anything else.
export function normalizeUrl(input) {
  const s = String(input || '').trim();
  if (!s) return '';
  let candidate = null;
  if (/^https?:\/\/\S+\.\S+$/i.test(s)) candidate = s;
  else if (/^(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i.test(s)) candidate = 'https://' + s;
  if (!candidate) return null;
  try {
    const u = new URL(candidate);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
  } catch {
    return null;
  }
  return stripTracking(candidate);
}

// Safe value for an href: only http(s) links survive, everything else renders no link.
export function safeHref(link) {
  const s = String(link || '').trim();
  if (!/^https?:\/\//i.test(s)) return '';
  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:' ? s : '';
  } catch {
    return '';
  }
}

export function hostOf(u) {
  try {
    return new URL(u).hostname.replace(/^www\./, '');
  } catch {
    return u;
  }
}

// Takes the first link out of a line (full URL or bare domain) and tidies what is left.
export function pullLink(line) {
  let s = line;
  let link = '';
  const m = s.match(URL_RE);
  const d = !m && s.match(DOMAIN_RE);
  if (m) {
    link = cleanLink(m[0]);
    s = s.replace(m[0], ' ');
  } else if (d) {
    link = 'https://' + cleanLink(d[2]);
    s = s.replace(d[0], d[1] + ' ');
  }
  if (link) {
    s = s
      .replace(/\(\s*\)|\[\s*\]|<\s*>/g, ' ')
      .replace(/\s+(?:at|from|on|via|here|link|url)\s*(?=[-–—:,;]|$)/gi, ' ')
      .replace(/\s*[-–—:,;]+\s*$/, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  return { s, link };
}

function stripLead(line) {
  return line
    .replace(BULLET, '')
    .replace(/^[\s ]+/, '')
    .replace(/^[\p{Extended_Pictographic}️‍\s]+/u, '')
    .trim();
}

export function isHeader(s) {
  if (URL_RE.test(s)) return false;
  if (/:\s*$/.test(s)) return true;
  return s.split(/\s+/).length <= 7 && /\b(christmas|holiday|wish|gift)\s*(list|ideas)\b/i.test(s);
}

export function looksLikeJunk(s) {
  return JUNK.test(s) || isHeader(s);
}

// Makes sure any URL ends up in `link` and not in the title or notes. Works on one { title, notes, link } item.
// A link the model already gave is normalized; one that is not valid is discarded (and searched for in the text).
export function tidyItem(item) {
  let title = String(item.title || '').trim();
  let notes = String(item.notes || '').trim();
  let link = normalizeUrl(item.link) || '';
  if (!link && item.link) {
    const found = pullLink(String(item.link));
    link = normalizeUrl(found.link) || '';
  }
  const fromTitle = pullLink(title);
  title = fromTitle.s;
  const fromNotes = pullLink(notes);
  notes = fromNotes.s;
  if (!link) link = normalizeUrl(fromTitle.link || fromNotes.link) || '';
  if (!title) title = link ? hostOf(link) : '';
  return { title, notes, link };
}

// Only touches the first letter, and only when the whole line is lowercase, so brand casing survives.
export function capitalizeFirst(s) {
  return s === s.toLowerCase() ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// Fallback when the AI call fails: one line, one gift. Odd rows get a `maybe` flag.
export function simpleSplit(raw) {
  const out = [];
  String(raw || '').replace(/\r/g, '').split('\n').forEach(line => {
    let s = stripLead(line);
    if (!s) return;
    const pl = pullLink(s);
    s = pl.s;
    const link = pl.link;
    if (link && (!s || LINKLABEL.test(s)) && out.length && !out[out.length - 1].link) {
      out[out.length - 1].link = link;
      return;
    }
    if (!s) s = hostOf(link);
    out.push({ title: capitalizeFirst(s), notes: '', link, maybe: looksLikeJunk(s) });
  });
  return out;
}
